import { useState, useEffect } from 'react';
import {
  INITIAL_COMPANIES,
  INITIAL_WORKSPACES,
  INITIAL_USERS,
  DEFAULT_NEW_MODEL,
  DEFAULT_NEW_WORKSPACE,
} from '../utils/mockdata.js';
import { userService, companyService, groupService, workspaceService } from '../../../services/index.js';

/**
 * useAdminState
 *
 * Single source of truth for the entire AdminDashboard.
 * Returns all state slices and their handlers so child components stay
 * stateless and receive only what they need via props.
 */
export function useAdminState() {
  // ── Navigation ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('companies');

  // ── Toast ────────────────────────────────────────────────────────────────
  const [showToast, setShowToast] = useState(false);
  const [apiMessage, setApiMessage] = useState('');
  const [success, setSuccess] = useState(true);

  const triggerToast = (ms = 4000) => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), ms);
  };

  // ── Company Management ───────────────────────────────────────────────────
  const [companiesData, setCompaniesData] = useState([]);
  const [searchQuery,        setSearchQuery]        = useState('');
  const [isOnboarding,       setIsOnboarding]       = useState(false);
  const [selectedCompany,    setSelectedCompany]    = useState(null);
  const [onboardStep,        setOnboardStep]        = useState(1);

  const handleCancelCompanyOnboarding = () => {
    setIsOnboarding(false);
    setOnboardStep(1);
  };

  const handleFinishCompanyOnboarding = () => {
    setIsOnboarding(false);
    setOnboardStep(1);
    setActiveTab('companies');
    triggerToast(5000);
    getCompanyList();
    setSelectedCompany(null);
    setApiMessage('Company Onboarded Successfully');
  };

  // ── User Management ──────────────────────────────────────────────────────
  const [usersData, setUserData] = useState([]);
  const [userSearchQuery,    setUserSearchQuery]    = useState('');
  const [isUserOnboarding,   setIsUserOnboarding]  = useState(false);
  const [selectedUserDetails,setSelectedUserDetails]= useState(null);
  const [userOnboardStep,    setUserOnboardStep]    = useState(1);

  const [groups, setGroups] = useState([]);

  const handleCancelUserOnboarding = () => {
    setIsUserOnboarding(false);
    setUserOnboardStep(1);
  };

  const handleFinishUserOnboarding = async (formData, forUpdate = false) => {
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          data.append(key, formData[key]);
        }
      });
      if (forUpdate) {
        data.append('id', selectedUserDetails.id);
        const res = await userService.update(data);
        if (res) {
          setSelectedUserDetails(null);
        }
      } else {
        await userService.create(data);
      }
      setIsUserOnboarding(false);
      setUserOnboardStep(1);
      setActiveTab('users');
      getUserList();
      triggerToast(5000);
      setSuccess(true);
      setApiMessage(forUpdate ? 'User Updated Successfully' : 'User Created Successfully');
    } catch (error) {
      triggerToast(5000);
      setApiMessage(error.message || 'Failed to save user. Please try again.');
      setSuccess(false);
    }
  };
  
  const getUserList = async() => {
    const data = await userService.getAllUsers();
    if(data) {
      setUserData(data.data);
    }
  }
  const getCompanyList = async() => {
    const data = await companyService.getAllCompanies();
    if(data) {
      setCompaniesData(data.data);
    }
  };
  const fetchGroups = async () => {
    const data = await groupService.getAllGroups();
    if (data) {
      setGroups(data);
    }
  };
  // ── Workspace Management ─────────────────────────────────────────────────
  const [workspacesData, setWorkspacesData] = useState([]);
  const [workspaceSearchQuery,    setWorkspaceSearchQuery]    = useState('');
  const [isWorkspaceOnboarding,   setIsWorkspaceOnboarding]  = useState(false);
  const [workspaceOnboardStep,    setWorkspaceOnboardStep]   = useState(1);
  const [newWorkspaceData,        setNewWorkspaceData]        = useState(DEFAULT_NEW_WORKSPACE);
  const [selectedWorkspaceDetails,setSelectedWorkspaceDetails]= useState(null);
  const [isWorkspaceMappingOnboarding, setIsWorkspaceMappingOnboarding] = useState(false);
  const [selectedWorkspaceMapping, setSelectedWorkspaceMapping] = useState(null);
  const [isRoleOnboarding, setIsRoleOnboarding] = useState(false);
  const [selectedRoleDetails, setSelectedRoleDetails] = useState(null);
  const handleCancelWorkspaceOnboarding = () => {
    setIsWorkspaceOnboarding(false);
    setWorkspaceOnboardStep(1);
    setNewWorkspaceData(DEFAULT_NEW_WORKSPACE);
    setSelectedWorkspaceDetails(null);
  };

  const handleFinishWorkspaceOnboarding = async (data) => {
    const finalData = {
      ...data,
      useCaseDetails: data.useCaseDetails.map((d) => {
        if (d.selectedService.toLowerCase() === 'sqlanalytics') {
          return { ...d, selectedFields: d.selectedFields.join(',') };
        }
        return d;
      }),
    };

    if (!selectedWorkspaceDetails) {
      await workspaceService.create(finalData);
      setApiMessage('Workspace Created Successfully');
    } else {
      await workspaceService.update({
        ...finalData,
        useCaseId: selectedWorkspaceDetails.useCaseId,
      });
      setApiMessage('Workspace Updated Successfully');
    }

    setIsWorkspaceOnboarding(false);
    setWorkspaceOnboardStep(1);
    setActiveTab('workspaces');
    setNewWorkspaceData(DEFAULT_NEW_WORKSPACE);
    setSelectedWorkspaceDetails(null);
    fetchWorkWorkspaces();
    triggerToast(5000);
    setSelectedCompany(null);
  };

  const fetchWorkWorkspaces = async() => {
    const data = await workspaceService.getWorkSpaceList({
      companyID: 0, // Pass 0 or appropriate company ID to fetch all workspaces
    })
    setWorkspacesData(data.data)
  }

  // ── Data Sources ─────────────────────────────────────────────────────────
  const [selectedDataCompanyId, setSelectedDataCompanyId] = useState('1');

  // ── System Settings ──────────────────────────────────────────────────────
  const [isAddingModel, setIsAddingModel] = useState(false);
  const [systemModels,  setSystemModels]  = useState([]);
  const [newModelData,  setNewModelData]  = useState(DEFAULT_NEW_MODEL);

  const handleAddModel = () => {
    setSystemModels(prev => [...prev, { ...newModelData, id: `m${Date.now()}`, status: 'active' }]);
    setIsAddingModel(false);
    setNewModelData(DEFAULT_NEW_MODEL);
    triggerToast();
  };

  const handleCancelAddModel = () => {
    setIsAddingModel(false);
    setNewModelData(DEFAULT_NEW_MODEL);
  };

  // ── Global nav handler (resets all sub-views) ────────────────────────────
  const handleNavChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedCompany(null);
    setSelectedUserDetails(null);
    setIsUserOnboarding(false);
    setIsOnboarding(false);
    setIsWorkspaceOnboarding(false);
    setIsWorkspaceMappingOnboarding(false);
    setSelectedWorkspaceMapping(null);
    setIsRoleOnboarding(false);
    setSelectedRoleDetails(null);
  };
  useEffect(() => {
    getUserList();
    getCompanyList();
    fetchGroups();
    fetchWorkWorkspaces();
  },[]);
  return {
    // nav
    activeTab, handleNavChange,

    // toast
    showToast, triggerToast,
    apiMessage, success, setApiMessage, setSuccess,

    // companies
    companiesData, searchQuery, setSearchQuery,
    isOnboarding, setIsOnboarding,
    selectedCompany, setSelectedCompany,
    onboardStep, setOnboardStep, getCompanyList,
    handleCancelCompanyOnboarding, handleFinishCompanyOnboarding,

    // users
    usersData, userSearchQuery, setUserSearchQuery,
    isUserOnboarding, setIsUserOnboarding,
    selectedUserDetails, setSelectedUserDetails,
    userOnboardStep, setUserOnboardStep,
    handleCancelUserOnboarding, handleFinishUserOnboarding,
    getUserList, groups, fetchGroups,

    // workspaces
    workspacesData, workspaceSearchQuery, setWorkspaceSearchQuery,
    isWorkspaceOnboarding, setIsWorkspaceOnboarding,
    workspaceOnboardStep, setWorkspaceOnboardStep,
    newWorkspaceData, setNewWorkspaceData,
    selectedWorkspaceDetails, setSelectedWorkspaceDetails,
    handleCancelWorkspaceOnboarding, handleFinishWorkspaceOnboarding,
    fetchWorkWorkspaces,
    isWorkspaceMappingOnboarding, setIsWorkspaceMappingOnboarding,
    selectedWorkspaceMapping, setSelectedWorkspaceMapping,
    isRoleOnboarding, setIsRoleOnboarding,
    selectedRoleDetails, setSelectedRoleDetails,

    // data sources
    selectedDataCompanyId, setSelectedDataCompanyId,

    // settings
    isAddingModel, setIsAddingModel,
    systemModels, setSystemModels,
    newModelData, setNewModelData,
    handleAddModel, handleCancelAddModel,
    
  };
}
